/**
 * AdminPdfCachePage — Phase 2C.C-ext (2026-09-08, Republish 18)
 *
 * Page admin `/admin/pdf-cache` : audit + régénération sélective des PDFs
 * cachés en GridFS après un bump de `PDF_TEMPLATE_VERSION`.
 *
 * 3 états explicites (badges 3 couleurs) :
 *   - up_to_date (vert) : blob template_version == courant
 *   - obsolete  (orange) : blob template_version < courant
 *   - missing   (rouge) : pas de blob GridFS = anomalie affichée, jamais masquée
 *
 * Garde-fous UI + BACKEND :
 *   - Bouton "Régénérer" désactivé sur devis signé + facture (émise par nature)
 *   - Backend refuse 403 côté serveur — la garde UI n'est pas la seule couche
 *   - Bulk "Régénérer tous obsolètes" : dry-run backend → modal affiche diff
 *     ligne par ligne (v4 → v6 par doc) → confirm exec réel
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

function StateBadge({ state, cached, current }) {
  if (state === "missing") {
    return <Badge className="border border-red-800 bg-red-950/50 text-red-300" title="Anomalie — blob GridFS absent">Jamais généré</Badge>;
  }
  if (state === "obsolete") {
    return <Badge className="border border-orange-700 bg-orange-950/50 text-orange-300" title={`Cache v${cached}, courant v${current}`}>Obsolète (v{cached ?? "?"} → v{current})</Badge>;
  }
  return <Badge className="border border-emerald-800 bg-emerald-950/40 text-emerald-300">À jour (v{cached})</Badge>;
}

function ActionBadge({ action }) {
  if (action === "regenerate") return <span className="text-emerald-400">Régénérer</span>;
  if (action === "skip_signed") return <span className="text-amber-400">Skip (signé)</span>;
  if (action === "skip_emitted_invoice") return <span className="text-amber-400">Skip (facture émise)</span>;
  return <span className="text-slate-500">{action}</span>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-BE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(iso).slice(0, 16); }
}

function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024*1024) return `${(bytes/1024).toFixed(1)} KB`;
  return `${(bytes/(1024*1024)).toFixed(1)} MB`;
}

export default function AdminPdfCachePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("all");
  const [state, setState] = useState("all");
  const [diffModal, setDiffModal] = useState(null); // { results, summary }
  const [executing, setExecuting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("type", type);
      params.set("state", state);
      params.set("page_size", "200");
      const r = await api.get(`/admin/pdf-cache?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [type, state]);

  useEffect(() => { load(); }, [load]);

  const openBulkDryRun = async () => {
    try {
      const r = await api.post("/admin/pdf-cache/regenerate", {
        target: "all_obsolete", dry_run: true,
      });
      setDiffModal(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const executeBulk = async () => {
    setExecuting(true);
    try {
      const r = await api.post("/admin/pdf-cache/regenerate", {
        target: "all_obsolete", dry_run: false,
      });
      toast.success(`${r.data.summary.to_regenerate} document(s) régénéré(s), ${r.data.summary.skipped_signed + r.data.summary.skipped_emitted} ignoré(s)`);
      setDiffModal(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExecuting(false); }
  };

  const regenSingle = async (id, docType) => {
    // Sanity : le backend refusera 403 aussi
    try {
      await api.post("/admin/pdf-cache/regenerate", {
        target: "ids", ids: [id], dry_run: false,
        // Phase 2C.C-ext (PDF v7) — force=true lève UNIQUEMENT le skip
        // `up_to_date` (utile quand une setting tenant change sans bump
        // template_version). Garde-fous signés/factures restent absolus.
        force: true,
      });
      toast.success("Document régénéré");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const items = data?.items || [];
  const currentV = data?.current_template_version;

  return (
    <div className="space-y-6" data-testid="admin-pdf-cache-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-100">Cache PDF — État & régénération</h1>
          <p className="text-sm text-slate-400">
            Version template courante : <b className="text-slate-100" data-testid="pdf-cache-current-version">v{currentV ?? "—"}</b>
            {" · "}
            <span className="text-slate-500">Les documents signés et les factures sont figés — jamais régénérés.</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="pdf-cache-refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-2">Rafraîchir</span>
          </Button>
          <Button onClick={openBulkDryRun} className="bg-[#C4553B] text-white hover:bg-[#A8402C]" data-testid="pdf-cache-bulk-btn">
            Régénérer tous les obsolètes
          </Button>
        </div>
      </div>

      <div className="flex gap-3">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-48" data-testid="pdf-cache-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="quote">Devis</SelectItem>
            <SelectItem value="invoice">Factures</SelectItem>
          </SelectContent>
        </Select>
        <Select value={state} onValueChange={setState}>
          <SelectTrigger className="w-56" data-testid="pdf-cache-filter-state"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous états</SelectItem>
            <SelectItem value="up_to_date">À jour</SelectItem>
            <SelectItem value="obsolete">Obsolète</SelectItem>
            <SelectItem value="missing">Jamais généré (anomalie)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-800 bg-slate-900/40">
        <CardHeader><CardTitle className="text-base text-slate-100">{items.length} document(s)</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="py-4 text-center text-sm text-slate-500">Aucun document.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Numéro</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">Statut</th>
                    <th className="px-3 py-2 text-left">État cache</th>
                    <th className="px-3 py-2 text-left">Client</th>
                    <th className="px-3 py-2 text-left">Généré</th>
                    <th className="px-3 py-2 text-right">Taille</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => {
                    const isSigned = r.type === "quote" && r.status === "signe";
                    const isInvoice = r.type === "invoice";
                    const disabled = isSigned || isInvoice;
                    const tip = isSigned ? "Devis signé — régénération interdite (document contractuel figé)"
                      : isInvoice ? "Facture émise figée comptablement — régénération interdite"
                      : "Régénérer ce document (purge + regen au prochain GET PDF)";
                    return (
                      <tr key={`${r.type}-${r.id}`} className="border-b border-slate-800/50 hover:bg-slate-950/40" data-testid={`pdf-cache-row-${r.id}`}>
                        <td className="px-3 py-2 font-mono text-slate-200">{r.number || r.id.slice(0,8)}</td>
                        <td className="px-3 py-2 text-slate-300">{r.type === "quote" ? "Devis" : "Facture"}</td>
                        <td className="px-3 py-2 text-slate-300">{r.status || "—"}</td>
                        <td className="px-3 py-2"><StateBadge state={r.state} cached={r.blob_template_version} current={currentV} /></td>
                        <td className="px-3 py-2 text-slate-300">{r.client_name}</td>
                        <td className="px-3 py-2 text-slate-400">{fmtDate(r.generated_at)}</td>
                        <td className="px-3 py-2 text-right text-slate-400">{fmtSize(r.size_bytes)}</td>
                        <td className="px-3 py-2 text-right">
                          <button type="button" onClick={() => !disabled && regenSingle(r.id, r.type)} disabled={disabled}
                            className={`rounded border px-2 py-1 text-xs transition-colors ${disabled ? "cursor-not-allowed border-slate-800 text-slate-600" : "border-slate-700 text-slate-300 hover:border-[#C4553B] hover:text-[#E9A98F]"}`}
                            title={tip} data-testid={`pdf-cache-regen-btn-${r.id}`}>
                            Régénérer
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!diffModal} onOpenChange={(v) => !v && setDiffModal(null)}>
        <DialogContent className="max-w-3xl" data-testid="pdf-cache-diff-dialog">
          <DialogHeader>
            <DialogTitle>Régénération en batch — dry-run</DialogTitle>
            <DialogDescription>
              Diff document par document. Les documents signés/factures sont automatiquement ignorés.
            </DialogDescription>
          </DialogHeader>
          {diffModal && (
            <>
              <div className="max-h-96 overflow-y-auto rounded border border-slate-800 bg-slate-950/40">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 border-b border-slate-800 bg-slate-900 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Numéro</th>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-left">Versions</th>
                      <th className="px-3 py-2 text-left">Action</th>
                      <th className="px-3 py-2 text-left">Raison</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diffModal.results.map((r) => (
                      <tr key={`${r.type}-${r.id}`} className="border-b border-slate-800/40" data-testid={`pdf-cache-diff-${r.id}`}>
                        <td className="px-3 py-1.5 font-mono text-slate-200">{r.number || r.id.slice(0,8)}</td>
                        <td className="px-3 py-1.5 text-slate-400">{r.type}</td>
                        <td className="px-3 py-1.5 font-mono text-slate-300">v{r.from_version ?? "?"} → v{r.to_version}</td>
                        <td className="px-3 py-1.5"><ActionBadge action={r.action} /></td>
                        <td className="px-3 py-1.5 text-slate-500">{r.reason || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="rounded border border-amber-900/40 bg-amber-950/20 p-3 text-xs text-amber-200">
                <ShieldAlert className="mr-1 inline h-3.5 w-3.5" />
                Résumé : <b>{diffModal.summary.to_regenerate}</b> à régénérer,
                {" "}<b>{diffModal.summary.skipped_signed}</b> devis signés ignorés,
                {" "}<b>{diffModal.summary.skipped_emitted}</b> factures ignorées,
                {" "}<b>{diffModal.summary.skipped_up_to_date}</b> déjà à jour.
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDiffModal(null)} disabled={executing} data-testid="pdf-cache-diff-cancel">Annuler</Button>
            <Button onClick={executeBulk} disabled={executing || !diffModal || diffModal.summary.to_regenerate === 0}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C]" data-testid="pdf-cache-diff-confirm">
              {executing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Confirmer la régénération de {diffModal?.summary.to_regenerate || 0} document(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
